import { useCallback, useEffect, useMemo, useRef } from 'react';

import { setCurrentPosition, setCollisionData } from '../use-positioning/actions';
import { CollisionDetectionService } from '../../services/collision-detection-service';
import { getNextPosition } from './get-next-position';
import { PositionService } from '../../services/position-service';
import { TooltipService } from '../../services/tooltip-service';
import { usePositioningReducer } from '../use-positioning/reducer';
import { useMaxZ } from '../services/use-max-z';
import { useWindowBoundingBox } from '../use-window-bounding-box';

const validPositions = TooltipService.getValidPositions();

export const usePositioning = (ref, startingPosition) => {
  const tooltipRef = useRef();
  const [state, dispatch] = usePositioningReducer(startingPosition);
  const { currentPosition, collisionData } = state;
  const windowBoundingBox = useWindowBoundingBox();
  const reset = useCallback(() => {
    dispatch(setCurrentPosition(startingPosition));
    dispatch(setCollisionData([]));
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    reset();
    // eslint-disable-next-line
  }, [windowBoundingBox]);

  useEffect(() => {
    const tooltipBoundingBox = tooltipRef.current.getBoundingClientRect();
    const allCollisions = collisionData.concat(CollisionDetectionService.getCollisions(tooltipBoundingBox, windowBoundingBox, true));
    const nextPosition = getNextPosition(allCollisions, currentPosition);

    if (nextPosition !== currentPosition) {
      dispatch(setCollisionData(allCollisions));
      dispatch(setCurrentPosition(nextPosition));
    }
  });

  const zIndex = useMaxZ();
  const { left, top } = PositionService.getStyleFromBoundingBox(currentPosition, ref.current.getBoundingClientRect(), validPositions);
  const style = useMemo(
    () => ({
      left,
      top,
      zIndex,
    }),
    [left, top, zIndex],
  );

  return {
    position: currentPosition,
    ref: tooltipRef,
    style,
  };
};
